import React, {UIEventHandler, useEffect, useState} from 'react';
import styled from "styled-components";
import {H4, H5, P1} from "../shared/fonts/fonts";
import {useDispatch, useSelector} from "react-redux";
import {storeType} from "../../redux/store";
import {AddChats, ChooseChat, ClearChats} from "../../redux/actions";
import {getNextChats as getNextChatsAPI} from "../../api/methods/messages/get/getChats";
import seeChatsAPI from "../../api/methods/messages/update/seeChats";
import setChatsStateAPI from "../../api/methods/messages/update/setChatsState";
import {useLocation} from "react-router-dom";
import {ButtonGray, ButtonPU, ButtonText} from "../shared/buttons/buttons";
import {Images} from "../../utils/images";
import User, {UserChatState, UserData} from "../../entities/User";
import MessageDate from "./MessageDate";
import {Checkbox} from "../shared/form/checkbox";
import {getUser} from "../../api/methods/getUser";
import {AdminUser} from "../../utils/constants";

const UserPhoto = styled.div<{src: string, count?: number}>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  
  background: ${props => props.src ? `url("${props.src}"), ` : ''}url("${Images.icons.avatar}");
  background-size: cover;
  background-position: center;
  position: relative;
  
  &::after {
    display: ${props => props.count ? 'grid' : 'none'};
    content: "${props => props.count && '+' + props.count}";
    position: absolute;
    
    right: -4px;
    top: -4px;
    font-size: 10px;
    background: ${props => props.theme.color.pi2};
    color: white;
    padding: 0 2px;
    border-radius: 4px;
  }
`;

const DialogStyle = styled.div`
    display: grid;
    grid-auto-flow: column;
    //justify-content: left;
    grid-template-columns: auto auto;
    grid-gap: 20px;
    align-items: center;
    background: white;
    padding: 0 24px;
    cursor: pointer;

    &.current {
        background: ${props => props.theme.color.dpu5};
    }
    
    &.checker {
        grid-template-columns: min-content auto auto;
    }
  
    & > a {
        justify-self: end;
    }
`;

const DialogClickableStyle = styled.div`
    display: grid;
    grid-auto-flow: column;
    grid-gap: 20px;
    align-items: center;
    grid-template-columns: min-content auto;
    padding: 18px 0;
`;

const Dialog = (props: {chat: User, checker: boolean,
    checkedUserIDs: number[], setCheckedUserID: (user_id: number, checked: boolean) => void}) => {
    const dispatch = useDispatch();
    const currentChatData = useSelector<storeType, UserData|null>((state) => state.currentChat);
    const currentChat = currentChatData ? new User(currentChatData) : null;
    const set = () => {
        dispatch(ChooseChat(props.chat.getValue()));
        //getChats();
    }

    const checked = props.checkedUserIDs.includes(props.chat.id);

    const classes: string[] = [];
    if (props.chat.id === currentChat?.id) classes.push('current');
    if (props.checker) classes.push('checker');

    return (
        <DialogStyle className={classes.join(' ')}>
            {
                props.checker &&
                <Checkbox value={checked}
                          onChange={() => props.setCheckedUserID(props.chat.id, !checked)} />
            }
            <DialogClickableStyle onClick={set}>
                <UserPhoto src={props.chat.avatar()} count={props.chat.unread} />
                <div>
                    <P1>{[props.chat.lastname, props.chat.firstname, props.chat.fatherName].join(' ')}</P1>
                    <MessageDate date={props.chat.last_message_timestamp} />
                </div>
            </DialogClickableStyle>
            <ButtonText href={props.chat.linkToView()}><H5>Перейти</H5></ButtonText>
        </DialogStyle>
    );
}

const DialogsStyle = styled.div`
    display: grid;
    grid-template-rows: min-content auto;
    overflow-x: hidden;
    align-content: start;
  
    & h4 {
        margin: 20px 24px;
    }
`;

const Container = styled.div`
  display: grid;
  border-top: 1px solid ${props => props.theme.color.dpu5};
  border-bottom: 1px solid ${props => props.theme.color.dpu5};
  overflow-y: scroll;
`;

const DialogsHeaderStyle = styled.div`
    display: grid;
    grid-auto-flow: column;
    align-items: center;
    margin: 0 10px;
    grid-template-columns: min-content auto auto;
`;

const DialogsHeaderStateStyle = styled.div`
    display: grid;
    grid-auto-flow: column;
    grid-gap: 10px;
    justify-content: end;
`;

const ActionsBlock = styled.div`
    display: grid;
    max-height: 0;
    transition: all 0.3s ease-out;
    justify-content: start;
    padding: 0 10px;
    grid-gap: 10px;
    overflow: hidden;
    
    &.expanded {
        padding: 0 10px 10px 10px;
        max-height: 150px;
    }
`;

const Dialogs = () => {
    const meData = useSelector<storeType, UserData | null>((state) => state.me);
    const me = meData ? new User(meData) : null;
    const chatsData = useSelector<storeType, UserData[]>((state) => state.chats);
    const chats = chatsData.map(c => new User(c));
    const currentChatData = useSelector<storeType, UserData|null>((state) => state.currentChat);
    const currentChat = currentChatData ? new User(currentChatData) : null;
    const dispatch = useDispatch();
    const [chatsLoading, setChatsLoading] = useState(false);
    const [state, setState] =
        useState<UserChatState>(UserChatState.active);
    const [showActions, setShowActions] =
        useState<boolean>(false);
    const [checkedUserIDs, setCheckedUserIDs] =
        useState<number[]>([]);
    const [defaultChatLoaded, setDefaultChatLoaded] =
        useState<boolean>(false);

    const setCheckedUserID = (user_id: number, checked: boolean) => {
        if (checked) {
            if (!checkedUserIDs.includes(user_id)) {
                setCheckedUserIDs([...checkedUserIDs, user_id]);
            }
        }
        else {
            setCheckedUserIDs(checkedUserIDs.filter(id => id !== user_id));
        }
    }

    const scroll:UIEventHandler<HTMLDivElement> = () => {
        const dialogs = document.getElementsByClassName('dialogs')[0].getBoundingClientRect();
        const dialogs_container = document.getElementsByClassName('dialogs-container')[0].getBoundingClientRect();
        //console.log(dialogs, dialogs_container);
        if (dialogs_container.height / 2 + dialogs_container.bottom > dialogs.bottom) {
            getNextChats();
        }
    }

    const getNextChats = () => {
        if (!me || !me.role.isAdmin()) return;
        if (!chatsLoading) {
            setChatsLoading(true);
            getNextChatsAPI(state)
                .finally(() => setChatsLoading(false));
        }
    }

    useEffect(() => {
        getNextChats();
        if (!me || !me.role.isAdmin()) {
            const chat_ = AdminUser;
            dispatch(AddChats([chat_.getValue()]));
            dispatch(ChooseChat(chat_.getValue()));
        }
    }, [meData]);

    useEffect(() => {
        setCheckedUserIDs([]);
    }, [showActions]);

    const refreshChats = () => {
        dispatch(ClearChats());
        getNextChats();
        setCheckedUserIDs([]);
    }

    useEffect(() => {
        refreshChats();
    }, [state]);

    const location = useLocation();
    useEffect(() => {
        if (!defaultChatLoaded && me) {
            const user_id_str = (new URLSearchParams(location.search).get('id')) || '';
            const user_id = /^[0-9]+$/.test(user_id_str)
                ? parseInt(user_id_str) : undefined;
            if (user_id && user_id !== me.id) {
                const chat_to_choose = chats.find(c => c.id === user_id);
                if (chat_to_choose) {
                    dispatch(ChooseChat(chat_to_choose.getValue()));
                } else {
                    getUser(user_id).then(user =>
                        dispatch(ChooseChat(new User(user).getValue())));
                }
                setDefaultChatLoaded(true);
            }
        }
    }, [chats, currentChatData, location.search, meData]);

    const seeChats = () =>
        seeChatsAPI(checkedUserIDs)
            .then(refreshChats);

    const setChatsState = () =>
        setChatsStateAPI(checkedUserIDs, state === UserChatState.active
            ? UserChatState.archive
            : UserChatState.active)
            .then(refreshChats);

    return (
        <DialogsStyle>
            <DialogsHeaderStyle>
                {
                    !showActions &&
                    <ButtonPU onClick={() => setShowActions(!showActions)}>
                        <H5>▼</H5>
                    </ButtonPU>
                }
                {
                    showActions &&
                    <ButtonPU onClick={() => setShowActions(!showActions)}>
                        <H5>✖</H5>
                    </ButtonPU>
                }
                <H4>Диалоги</H4>
                <DialogsHeaderStateStyle>
                    {
                        state === UserChatState.active &&
                        <>
                            <ButtonPU><H5>Активные</H5></ButtonPU>
                            <ButtonGray onClick={() => setState(UserChatState.archive)}>
                                <H5>Архив</H5>
                            </ButtonGray>
                        </>
                    }
                    {
                        state === UserChatState.archive &&
                        <>
                            <ButtonGray onClick={() => setState(UserChatState.active)}>
                                <H5>Активные</H5>
                            </ButtonGray>
                            <ButtonPU><H5>Архив</H5></ButtonPU>
                        </>
                    }
                </DialogsHeaderStateStyle>
            </DialogsHeaderStyle>
            <ActionsBlock className={showActions ? 'expanded' : undefined}>
                <ButtonGray onClick={seeChats}><H5>Отметить прочитанными</H5></ButtonGray>
                <ButtonGray onClick={setChatsState}>
                    {
                        state === UserChatState.active
                            ? <H5>Переместить в архив</H5>
                            : <H5>Восстановить чат</H5>
                    }
                </ButtonGray>
            </ActionsBlock>
            <Container className={'dialogs-container'} onScroll={scroll}>
                <div className={'dialogs'}>
                    {
                        chats.map((chat, i) =>
                            <Dialog key={i} chat={chat} checker={showActions}
                                    checkedUserIDs={checkedUserIDs}
                                    setCheckedUserID={setCheckedUserID} />)
                    }
                </div>
            </Container>
        </DialogsStyle>
    );
};

export default Dialogs;
